// src/ClinicEventSignup.js
import React, { useEffect, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import styles from "./ActiveClinicEvents.module.css";

import { db, auth } from "./firebase";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  getDoc,
} from "firebase/firestore";

// Utility to convert 24-hour time to 12-hour format
const formatTime = (timeStr) => {
  const [hour, minute] = timeStr.split(":").map(Number);
  const ampm = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${minute.toString().padStart(2, "0")} ${ampm}`;
};

function ClinicEventSignup() {
  const [allEvents, setAllEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [userEmail, setUserEmail] = useState(null);
  const [eventToSignUp, setEventToSignUp] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [userCohort, setUserCohort] = useState(null);

  useEffect(() => {
    const fetchEventsAndUser = async () => {
      const user = auth.currentUser;
      if (!user) return;

      setUserEmail(user.email);

      const userDoc = await getDoc(doc(db, "users", user.email));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setUserCohort(data.cohort);
      }

      const querySnapshot = await getDocs(collection(db, "clinic_events"));
      const eventsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAllEvents(eventsData);
    };

    fetchEventsAndUser();
  }, []);

  const handleSignUp = async () => {
    const eventRef = doc(db, "clinic_events", eventToSignUp);
    const updatedEvent = allEvents.find((e) => e.id === eventToSignUp);
    const updatedSignedUp = [...(updatedEvent.signedUp || []), userEmail];

    await updateDoc(eventRef, {
      signedUp: updatedSignedUp,
    });

    setAllEvents((prev) =>
      prev.map((e) =>
        e.id === eventToSignUp ? { ...e, signedUp: updatedSignedUp } : e
      )
    );

    setShowConfirmModal(false);
    setEventToSignUp(null);
  };

  const selectedMonth = selectedDate.getMonth();
  const selectedYear = selectedDate.getFullYear();

  const monthlyEvents = allEvents.filter((event) => {
    const [year, month] = event.date.split("-").map(Number);
    const isMatchingMonth = year === selectedYear && month - 1 === selectedMonth;
    const isMatchingCohort = event.cohorts?.includes(userCohort);
    return isMatchingMonth && isMatchingCohort;
  });

  const monthName = selectedDate.toLocaleString("default", { month: "long" });


  return (
    <div className={styles.container}>
      <h2 className={styles.header}>📝 Clinic Event Sign-Up</h2>

      {showConfirmModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.confirmModal}>
            <h3>Confirm Sign-Up</h3>
            <p>Are you sure you want to sign up for this event?</p>
            <div className={styles.modalButtons}>
              <button className={styles.confirmBtn} onClick={handleSignUp}>
                Yes, Sign Me Up
              </button>
              <button
                className={styles.cancelBtn}
                onClick={() => setShowConfirmModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.mainContent}>
        <div className={styles.calendarPane}>
          <Calendar
            className={styles.bigCalendar}
            onClickDay={setSelectedDate}
            value={selectedDate}
            tileContent={({ date, view }) => {
              const dateStr = date.toISOString().split("T")[0];
              const matchingEvents = allEvents.filter((e) => e.date === dateStr);
              if (view === "month" && matchingEvents.length > 0) {
                return (
                  <div className={styles.eventDotContainer}>
                    {matchingEvents.map((_, idx) => (
                      <div key={idx} className={styles.eventDot} />
                    ))}
                  </div>
                );
              }
              return null;
            }}
          />
        </div>

        <div className={styles.cardPane}>
            <h3 className={styles.subHeader}>
  Events in {monthName} {selectedYear}
</h3>

          {monthlyEvents.length === 0 ? (
            <p>No events available this month.</p>
          ) : (
            monthlyEvents.map((event) => {
              const signedUp = event.signedUp || [];
              const spotsLeft = event.maxStudents - signedUp.length;
              const alreadySignedUp = signedUp.includes(userEmail);

              return (
                <div
  key={event.id}
  className={`${styles.eventCard} ${
    alreadySignedUp
      ? styles.cardSignedUp
      : spotsLeft > 0
      ? styles.cardAvailable
      : styles.cardFull
  }`}
>

                  {!alreadySignedUp && (
                    <div className={styles.cancelButtonWrapper}>
  {spotsLeft > 0 ? (
    <button
      onClick={() => {
        setEventToSignUp(event.id);
        setShowConfirmModal(true);
      }}
      className={styles.signUpButton}
    >
      Sign Up
    </button>
  ) : (
    <span className={styles.fullLabel}>🚫 Event Full</span>
  )}
</div>

                  )}
                  {alreadySignedUp && (
                    <div className={styles.tag}>✅ You're Signed Up</div>
                  )}

                  <h3>{event.title}</h3>
                  <p><strong>📍 Location:</strong> {event.location}</p>
                  <p><strong>📅 Date:</strong> {event.date}</p>
                  <p><strong>🕒 Time:</strong> {formatTime(event.startTime)} – {formatTime(event.endTime)}</p>
                  <p><strong>👥 Max Students:</strong> {event.maxStudents}</p>
                  <div className={styles.spotRow}>
  <span className={styles.filledPill}>Filled: {signedUp.length}</span>
  <span className={styles.leftPill}>Left: {spotsLeft}</span>
</div>
                  {event.notes && <p><strong>🧾 Notes:</strong> {event.notes}</p>}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default ClinicEventSignup;
